import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { internshipApi } from '@/api';
import {
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Pagination,
  Select,
  Spinner,
} from '@/components/ui';
import { InternshipCard } from '@/features/internships/InternshipCard';
import { useSaveInternship } from '@/features/internships/useSaveInternship';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

const WORK_MODES = ['ONSITE', 'REMOTE', 'HYBRID'];
const PAGE_SIZE = 10;
/** Month choices for the duration range; the API accepts 0-36. */
const DURATION_MONTHS = [1, 2, 3, 4, 6, 9, 12];
/** "Closing within N days" choices, turned into `deadline_before`. */
const CLOSING_WITHIN_DAYS = [7, 14, 30];

/** Today plus `days`, as the `YYYY-MM-DD` the API expects (local calendar). */
function isoDateInDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function numberParam(value) {
  return value ? Number(value) : undefined;
}

/** "1-6 months", "At least 3 months", "Up to 6 months". */
function durationLabel(min, max) {
  const unit = (value) => (value === 1 ? 'month' : 'months');
  if (min != null && max != null) {
    return min === max ? `${min} ${unit(min)}` : `${min}–${max} months`;
  }
  if (min != null) return `At least ${min} ${unit(min)}`;
  return `Up to ${max} ${unit(max)}`;
}

/**
 * Internship discovery.
 *
 * Filters live in the URL, so a search can be shared and the back button
 * works. Every filter is applied server-side.
 */
/** "HYBRID" -> "Hybrid". */
function titleCase(value) {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

export function InternshipsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get('q') ?? '');
  // On a phone the panel starts closed, so the results are what you see first.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const save = useSaveInternship();

  const filters = useMemo(() => {
    const workModes = searchParams.getAll('work_modes');
    const skillIds = searchParams.getAll('skill_ids');
    const q = searchParams.get('q') ?? undefined;
    // The URL keeps "closing within N days" rather than a fixed date, so a
    // shared link still means "closing soon" a week later.
    const closingWithin = numberParam(searchParams.get('closing_within'));
    const sort = searchParams.get('sort') ?? 'recent';
    return {
      q,
      skill_ids: skillIds.length ? skillIds : undefined,
      job_category_id: searchParams.get('job_category_id') ?? undefined,
      industry_id: searchParams.get('industry_id') ?? undefined,
      // Set by a company page's "N open roles" link.
      company_id: searchParams.get('company_id') ?? undefined,
      location: searchParams.get('location') ?? undefined,
      work_modes: workModes.length ? workModes : undefined,
      min_duration_months: numberParam(searchParams.get('min_duration_months')),
      max_duration_months: numberParam(searchParams.get('max_duration_months')),
      min_stipend: numberParam(searchParams.get('min_stipend')),
      paid_only: searchParams.get('paid_only') === 'true' || undefined,
      deadline_after: closingWithin ? isoDateInDays(0) : undefined,
      deadline_before: closingWithin ? isoDateInDays(closingWithin) : undefined,
      include_closed: searchParams.get('include_closed') === 'true' || undefined,
      // "Most relevant" ranks against the keyword, so without one it is
      // plain newest-first.
      sort: sort === 'relevance' && !q ? 'recent' : sort,
      page: Number(searchParams.get('page') ?? '1'),
      page_size: PAGE_SIZE,
    };
  }, [searchParams]);
  const closingWithin = numberParam(searchParams.get('closing_within'));

  // The sidebar counts take the same filters as the list (not the page or
  // the order), so "Onsite 7" means ticking it shows 7.
  const facetFilters = useMemo(() => {
    // eslint-disable-next-line no-unused-vars -- dropped on purpose
    const { page, page_size, sort, ...rest } = filters;
    return rest;
  }, [filters]);
  const { data: facets } = useQuery({
    queryKey: ['internship-facets', facetFilters],
    queryFn: () => internshipApi.getFacets(facetFilters),
    // Keep the old counts on screen while the new ones load, so the sidebar
    // does not blank out on every click.
    placeholderData: keepPreviousData,
  });

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['internships', filters],
    queryFn: () => internshipApi.searchInternships(filters),
    placeholderData: keepPreviousData,
  });

  function updateParams(changes) {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      next.delete(key);
      if (Array.isArray(value)) value.forEach((item) => next.append(key, item));
      else if (value) next.set(key, value);
    });
    // Any filter change resets to the first page.
    if (!('page' in changes)) next.delete('page');
    setSearchParams(next);
  }

  const activeWorkModes = searchParams.getAll('work_modes');

  const categoryName = facets?.job_categories.find(
    (facet) => facet.value === searchParams.get('job_category_id'),
  )?.label;
  const locationName = facets?.locations.find(
    (facet) => facet.value === searchParams.get('location'),
  )?.label;
  const industryName = facets?.industries.find(
    (facet) => facet.value === searchParams.get('industry_id'),
  )?.label;
  const activeSkillIds = searchParams.getAll('skill_ids');
  const skillName = (id) =>
    facets?.top_skills.find((facet) => facet.value === id)?.label ?? 'Skill';

  /** Every filter in force, as a chip that removes itself. */
  const chips = [
    filters.q && {
      key: 'q',
      label: `“${filters.q}”`,
      // "Most relevant" needs a keyword, so it goes with it.
      clear: () =>
        updateParams({
          q: undefined,
          ...(searchParams.get('sort') === 'relevance' && { sort: undefined }),
        }),
    },
    ...activeSkillIds.map((id) => ({
      key: `skill-${id}`,
      label: skillName(id),
      clear: () => updateParams({ skill_ids: activeSkillIds.filter((value) => value !== id) }),
    })),
    categoryName && {
      key: 'category',
      label: categoryName,
      clear: () => updateParams({ job_category_id: undefined }),
    },
    industryName && {
      key: 'industry',
      label: industryName,
      clear: () => updateParams({ industry_id: undefined }),
    },
    filters.company_id && {
      key: 'company',
      label: searchParams.get('company') ?? 'One company',
      clear: () => updateParams({ company_id: undefined, company: undefined }),
    },
    locationName && {
      key: 'location',
      label: locationName,
      clear: () => updateParams({ location: undefined }),
    },
    ...activeWorkModes.map((mode) => ({
      key: mode,
      label: titleCase(mode),
      clear: () => updateParams({ work_modes: activeWorkModes.filter((value) => value !== mode) }),
    })),
    filters.min_stipend && {
      key: 'min_stipend',
      label: `From ${filters.min_stipend}/month`,
      clear: () => updateParams({ min_stipend: undefined }),
    },
    filters.paid_only && {
      key: 'paid_only',
      label: 'Paid only',
      clear: () => updateParams({ paid_only: undefined }),
    },
    (filters.min_duration_months != null || filters.max_duration_months != null) && {
      key: 'duration',
      label: durationLabel(filters.min_duration_months, filters.max_duration_months),
      clear: () => updateParams({ min_duration_months: undefined, max_duration_months: undefined }),
    },
    closingWithin && {
      key: 'closing_within',
      label: `Closing within ${closingWithin} days`,
      clear: () => updateParams({ closing_within: undefined }),
    },
    filters.include_closed && {
      key: 'include_closed',
      label: 'Including closed',
      clear: () => updateParams({ include_closed: undefined }),
    },
  ].filter(Boolean);

  const total = data?.meta.total_items;

  return (
    // Signed in, on a phone or tablet held upright, the page takes the height
    // left under any banners and the results panel runs to the bottom of the
    // screen. (The public page has a footer below, so not there.)
    <div className="flex flex-col in-[.app-shell]:portrait:flex-1">
      {/* The banner: the same violet card as the dashboard greeting, with
          one big search bar and the live count -- the first thing a student
          wants to know is whether there is anything here for them. */}
      <section className="shell-hero rounded-card relative isolate overflow-hidden px-5 py-6 text-white sm:px-6">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-10 -z-10 size-64 rounded-full bg-white/10"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -bottom-32 -z-10 size-72 rounded-full bg-white/5"
        />
        <div className="relative">
          <h1 className="text-2xl font-semibold tracking-tight">Find your internship</h1>
          <p className="mt-0.5 text-sm text-white/80">
            {total != null
              ? `${total} open internship${total === 1 ? '' : 's'} from companies hiring right now.`
              : 'Search open internships from companies hiring right now.'}
          </p>

          <form
            role="search"
            className="mt-4 flex max-w-2xl flex-col gap-1.5 rounded-xl bg-white p-1 shadow-lg shadow-black/10 sm:flex-row sm:items-center"
            onSubmit={(event) => {
              event.preventDefault();
              updateParams({
                q: keyword || undefined,
                ...(!keyword && searchParams.get('sort') === 'relevance' && { sort: undefined }),
              });
            }}
          >
            <label className="flex min-w-0 flex-1 items-center gap-2 px-2.5">
              <Icon name="search" size={16} className="text-ink-muted shrink-0" />
              <span className="sr-only">Search internships</span>
              <input
                type="search"
                className="text-ink placeholder:text-ink-muted/70 h-8 min-w-0 flex-1 bg-transparent text-sm outline-none"
                placeholder="Job title, company or skill"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              />
            </label>
            <Button type="submit" size="sm" className="sm:px-5">
              Search
            </Button>
          </form>
        </div>
      </section>

      <div className="mt-6 flex flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-6 lg:grid lg:grid-cols-[280px_1fr] lg:items-start">
          {/* One panel, sections divided -- not four floating cards. It
              stays in view while the results scroll past. */}
          <aside className="bg-surface border-line rounded-card shadow-card border lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
            <div
              className={cn(
                'border-line flex items-center justify-between gap-3 px-5 py-4 lg:border-b',
                filtersOpen && 'border-b',
              )}
            >
              <h2 className="text-ink text-sm font-semibold">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((open) => !open)}
                  aria-expanded={filtersOpen}
                  aria-controls="internship-filters"
                  className="flex items-center gap-2 lg:pointer-events-none"
                >
                  <Icon name="filter" size={16} className="text-brand-600" />
                  Filters
                  {chips.length > 0 && (
                    <span className="bg-brand-600 rounded-full px-1.5 text-[11px] leading-4 text-white">
                      {chips.length}
                    </span>
                  )}
                  <Icon
                    name="chevron"
                    size={14}
                    className={cn(
                      'text-ink-muted transition-transform lg:hidden',
                      filtersOpen ? '-rotate-90' : 'rotate-90',
                    )}
                  />
                </button>
              </h2>
              {searchParams.toString() && (
                <button
                  type="button"
                  onClick={() => setSearchParams({})}
                  className="text-brand-700 text-xs font-semibold hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>

            <div
              id="internship-filters"
              className={cn('divide-line divide-y', !filtersOpen && 'max-lg:hidden')}
            >
              <div className="px-5 py-4" role="group" aria-labelledby="filter-work-mode">
                <p
                  id="filter-work-mode"
                  className="text-ink-muted mb-2 text-[11px] font-semibold tracking-wide uppercase"
                >
                  Work mode
                </p>
                <div className="space-y-1">
                  {WORK_MODES.map((mode) => {
                    const count =
                      facets?.work_modes.find((facet) => facet.label === mode)?.count ?? 0;
                    const checked = activeWorkModes.includes(mode);
                    return (
                      <label
                        key={mode}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm transition',
                          checked
                            ? 'bg-brand-50 text-brand-700'
                            : 'text-ink hover:bg-surface-muted',
                        )}
                      >
                        <input
                          type="checkbox"
                          className="border-line accent-brand-600 size-4 rounded border"
                          checked={checked}
                          onChange={(event) => {
                            const next = event.target.checked
                              ? [...activeWorkModes, mode]
                              : activeWorkModes.filter((value) => value !== mode);
                            updateParams({ work_modes: next });
                          }}
                        />
                        <span className="flex-1">{titleCase(mode)}</span>
                        <span className="text-ink-muted bg-surface-muted rounded-full px-2 text-[11px] font-semibold tabular-nums">
                          {count}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {facets?.top_skills.length > 0 && (
                <div className="px-5 py-4" role="group" aria-labelledby="filter-skills">
                  <p
                    id="filter-skills"
                    className="text-ink-muted mb-2 text-[11px] font-semibold tracking-wide uppercase"
                  >
                    Skills
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {facets.top_skills.map((facet) => {
                      const selected = activeSkillIds.includes(facet.value);
                      return (
                        <button
                          key={facet.value}
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            updateParams({
                              skill_ids: selected
                                ? activeSkillIds.filter((value) => value !== facet.value)
                                : [...activeSkillIds, facet.value],
                            })
                          }
                          className={cn(
                            'inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition',
                            selected
                              ? 'border-brand-600 bg-brand-600 text-white'
                              : 'border-line text-ink hover:border-brand-200 hover:bg-brand-50',
                          )}
                        >
                          {selected && <Icon name="check" size={12} className="shrink-0" />}
                          <span className="truncate">{facet.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-ink-muted mt-2 text-xs">
                    Shows listings that ask for any of these.
                  </p>
                </div>
              )}

              <div className="space-y-4 px-5 py-4">
                <Select
                  label="Category"
                  size="sm"
                  value={searchParams.get('job_category_id') ?? ''}
                  onChange={(event) =>
                    updateParams({ job_category_id: event.target.value || undefined })
                  }
                >
                  <option value="">All categories</option>
                  {facets?.job_categories.map((facet) => (
                    <option key={facet.value} value={facet.value}>
                      {facet.label} ({facet.count})
                    </option>
                  ))}
                </Select>

                <Select
                  label="Industry"
                  size="sm"
                  value={searchParams.get('industry_id') ?? ''}
                  onChange={(event) =>
                    updateParams({ industry_id: event.target.value || undefined })
                  }
                >
                  <option value="">All industries</option>
                  {facets?.industries.map((facet) => (
                    <option key={facet.value} value={facet.value}>
                      {facet.label} ({facet.count})
                    </option>
                  ))}
                </Select>

                <Select
                  label="Location"
                  size="sm"
                  value={searchParams.get('location') ?? ''}
                  onChange={(event) => updateParams({ location: event.target.value || undefined })}
                >
                  <option value="">Anywhere</option>
                  {facets?.locations.map((facet) => (
                    <option key={facet.value} value={facet.value}>
                      {facet.label} ({facet.count})
                    </option>
                  ))}
                </Select>
              </div>

              <div className="px-5 py-4">
                <Input
                  label="Minimum stipend"
                  type="number"
                  size="sm"
                  min={0}
                  step={50}
                  placeholder="Any amount"
                  defaultValue={searchParams.get('min_stipend') ?? ''}
                  onBlur={(event) => updateParams({ min_stipend: event.target.value || undefined })}
                />
                <label className="text-ink mt-3 flex cursor-pointer items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="border-line accent-brand-600 size-4 rounded border"
                    checked={searchParams.get('paid_only') === 'true'}
                    onChange={(event) =>
                      updateParams({ paid_only: event.target.checked ? 'true' : undefined })
                    }
                  />
                  Paid internships only
                </label>
              </div>

              <div className="px-5 py-4" role="group" aria-labelledby="filter-duration">
                <p
                  id="filter-duration"
                  className="text-ink-muted mb-2 text-[11px] font-semibold tracking-wide uppercase"
                >
                  Duration
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    label="Shortest"
                    size="sm"
                    value={searchParams.get('min_duration_months') ?? ''}
                    onChange={(event) =>
                      updateParams({ min_duration_months: event.target.value || undefined })
                    }
                  >
                    <option value="">Any</option>
                    {DURATION_MONTHS.filter(
                      (months) =>
                        filters.max_duration_months == null ||
                        months <= filters.max_duration_months,
                    ).map((months) => (
                      <option key={months} value={months}>
                        {months} mo
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="Longest"
                    size="sm"
                    value={searchParams.get('max_duration_months') ?? ''}
                    onChange={(event) =>
                      updateParams({ max_duration_months: event.target.value || undefined })
                    }
                  >
                    <option value="">Any</option>
                    {DURATION_MONTHS.filter(
                      (months) =>
                        filters.min_duration_months == null ||
                        months >= filters.min_duration_months,
                    ).map((months) => (
                      <option key={months} value={months}>
                        {months} mo
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="px-5 py-4">
                <Select
                  label="Application deadline"
                  size="sm"
                  value={closingWithin ? String(closingWithin) : ''}
                  onChange={(event) =>
                    updateParams({ closing_within: event.target.value || undefined })
                  }
                >
                  <option value="">Any time</option>
                  {CLOSING_WITHIN_DAYS.map((days) => (
                    <option key={days} value={days}>
                      Closing within {days} days
                    </option>
                  ))}
                </Select>
                <label className="text-ink mt-3 flex cursor-pointer items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="border-line accent-brand-600 size-4 rounded border"
                    checked={searchParams.get('include_closed') === 'true'}
                    onChange={(event) =>
                      updateParams({ include_closed: event.target.checked ? 'true' : undefined })
                    }
                  />
                  Show closed internships
                </label>
              </div>
            </div>
          </aside>

          <section className="flex min-w-0 flex-1 flex-col lg:self-stretch">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-ink text-sm" aria-live="polite">
                {total != null && (
                  <>
                    <span className="font-semibold tabular-nums">{total}</span> internship
                    {total === 1 ? '' : 's'}
                  </>
                )}
                {isFetching && !isPending && (
                  <span className="text-ink-muted ml-2 inline-flex items-center gap-1.5 text-xs">
                    <Spinner className="size-3 border" />
                    Updating
                  </span>
                )}
              </p>
              <Select
                label="Sort"
                hideLabel
                size="sm"
                wrapperClassName="w-48"
                value={filters.sort}
                onChange={(event) => updateParams({ sort: event.target.value })}
              >
                {filters.q && <option value="relevance">Most relevant</option>}
                <option value="recent">Newest first</option>
                <option value="deadline">Closing soon</option>
                <option value="stipend">Highest stipend</option>
              </Select>
            </div>

            {chips.length > 0 && (
              <ul className="mb-5 flex flex-wrap gap-2" aria-label="Active filters">
                {chips.map((chip) => (
                  <li key={chip.key}>
                    <button
                      type="button"
                      onClick={chip.clear}
                      aria-label={`Remove filter ${chip.label}`}
                      className="border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100 inline-flex items-center gap-1.5 rounded-full border py-1 pr-2 pl-3 text-xs font-medium transition"
                    >
                      {chip.label}
                      <Icon name="close" size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Upright, the results sit on one panel that takes the height
                left; the pager stays at its foot. */}
            <div className="flex flex-1 flex-col in-[.app-shell]:portrait:bg-surface in-[.app-shell]:portrait:border-line in-[.app-shell]:portrait:rounded-card in-[.app-shell]:portrait:shadow-card in-[.app-shell]:portrait:border in-[.app-shell]:portrait:p-4">
              {isPending && <LoadingState label="Loading internships…" />}

              {isError && (
                <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
              )}

              {data && data.data.length === 0 && (
                <div className="flex flex-1 flex-col *:flex-1 *:justify-center">
                  <EmptyState
                    icon={<Icon name="search" size={22} />}
                    title="No internships match these filters"
                    description="Try removing a filter or broadening your search."
                    action={
                      <Button variant="secondary" onClick={() => setSearchParams({})}>
                        Clear filters
                      </Button>
                    }
                  />
                </div>
              )}

              <div
                className={cn(
                  'space-y-4 transition-opacity',
                  isFetching && !isPending && 'opacity-70',
                )}
              >
                {data?.data.map((internship) => (
                  <InternshipCard
                    key={internship.id}
                    internship={internship}
                    onToggleSave={save.canSave ? save.toggle : undefined}
                    isSaving={save.isPending && save.pendingId === internship.id}
                  />
                ))}
              </div>

              {data && (
                <Pagination
                  className="mt-auto pt-6"
                  page={data.meta.page}
                  pageSize={PAGE_SIZE}
                  totalPages={data.meta.total_pages}
                  totalItems={data.meta.total_items}
                  onPageChange={(page) => updateParams({ page: String(page) })}
                />
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
